/** 侧栏 ECharts 暗色科技风公共配置 */

export const CHART_COLORS = [
  "#3de7ff",
  "#42a5f5",
  "#69f0ae",
  "#ffb300",
  "#ff7043",
  "#ab47bc",
  "#26c6da",
  "#ef5350",
];

export const tooltipDark = {
  backgroundColor: "rgba(6, 18, 32, 0.92)",
  borderColor: "rgba(61, 231, 255, 0.35)",
  textStyle: { color: "#d8f6ff", fontSize: 12 },
};

function hexToRgb(hex) {
  const h = String(hex || "").replace("#", "");
  const full =
    h.length === 3
      ? h
          .split("")
          .map((c) => c + c)
          .join("")
      : h;
  const n = parseInt(full, 16);
  if (!Number.isFinite(n)) return { r: 61, g: 231, b: 255 };
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function rgba(hex, a) {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r},${g},${b},${a})`;
}

/** 圆环扇区渐变：从里向外（径向） */
function pieSectorGradient(hex) {
  const { r, g, b } = hexToRgb(hex);
  const inner = `rgb(${Math.min(255, r + 56)},${Math.min(255, g + 56)},${Math.min(255, b + 40)})`;
  const outer = rgba(hex, 0.78);
  return {
    type: "radial",
    x: 0.5,
    y: 0.5,
    r: 1,
    colorStops: [
      { offset: 0, color: inner },
      { offset: 0.55, color: hex },
      { offset: 1, color: outer },
    ],
  };
}

export function pieOption(data, { title = "", ring = true, rose = false } = {}) {
  const items = (data || []).map((d, i) => {
    const hex = CHART_COLORS[i % CHART_COLORS.length];
    return {
      ...d,
      itemStyle: {
        color: pieSectorGradient(hex),
        shadowBlur: 12,
        shadowColor: rgba(hex, 0.45),
      },
    };
  });

  return {
    color: CHART_COLORS,
    tooltip: { ...tooltipDark, trigger: "item" },
    legend: {
      bottom: 0,
      left: "center",
      itemWidth: 8,
      itemHeight: 8,
      textStyle: { color: "rgba(216,246,255,0.65)", fontSize: 11 },
    },
    series: [
      {
        name: title,
        type: "pie",
        // 玫瑰图：半径随数值变化；普通环图保持内空
        radius: rose ? ["18%", "68%"] : ring ? ["42%", "68%"] : ["0%", "68%"],
        center: ["50%", "44%"],
        roseType: rose ? "radius" : undefined,
        avoidLabelOverlap: true,
        itemStyle: {
          borderColor: "rgba(2, 10, 18, 0.85)",
          borderWidth: 2,
          borderRadius: rose ? 4 : 3,
        },
        label: {
          color: "rgba(216,246,255,0.85)",
          fontSize: 11,
          formatter: "{b}\n{c}",
        },
        labelLine: {
          length: 8,
          length2: 6,
          lineStyle: { color: "rgba(61,231,255,0.35)" },
        },
        data: items,
      },
    ],
  };
}

export function barOption(categories, values, { unit = "", horizontal = true } = {}) {
  const baseAxis = {
    axisLine: { lineStyle: { color: "rgba(61,231,255,0.25)" } },
    axisLabel: { color: "rgba(216,246,255,0.65)", fontSize: 11 },
    splitLine: { lineStyle: { color: "rgba(61,231,255,0.08)" } },
  };

  return {
    color: CHART_COLORS,
    tooltip: {
      ...tooltipDark,
      trigger: "axis",
      valueFormatter: (v) => (unit ? `${v} ${unit}` : String(v)),
    },
    grid: {
      left: horizontal ? 72 : 36,
      right: 16,
      top: 16,
      bottom: 28,
    },
    xAxis: horizontal
      ? { type: "value", ...baseAxis }
      : { type: "category", data: categories, ...baseAxis },
    yAxis: horizontal
      ? {
          type: "category",
          data: categories,
          axisLabel: { color: "rgba(216,246,255,0.75)", fontSize: 11, width: 64, overflow: "truncate" },
          axisLine: { show: false },
          axisTick: { show: false },
        }
      : { type: "value", ...baseAxis },
    series: [
      {
        type: "bar",
        data: values,
        barWidth: 10,
        itemStyle: {
          borderRadius: horizontal ? [0, 4, 4, 0] : [4, 4, 0, 0],
          color: {
            type: "linear",
            x: horizontal ? 0 : 0,
            y: horizontal ? 0 : 1,
            x2: horizontal ? 1 : 0,
            y2: horizontal ? 0 : 0,
            colorStops: [
              { offset: 0, color: "rgba(61,231,255,0.35)" },
              { offset: 1, color: "#3de7ff" },
            ],
          },
        },
      },
    ],
  };
}

export function gaugeOption(value, { max = 100, name = "占比" } = {}) {
  const v = Math.max(0, Math.min(Number(value) || 0, max));
  return {
    series: [
      {
        type: "gauge",
        startAngle: 210,
        endAngle: -30,
        min: 0,
        max,
        radius: "95%",
        center: ["50%", "58%"],
        progress: {
          show: true,
          width: 10,
          roundCap: true,
          itemStyle: {
            color: {
              type: "linear",
              x: 0,
              y: 0,
              x2: 1,
              y2: 0,
              colorStops: [
                { offset: 0, color: "rgba(61,231,255,0)" },
                { offset: 0.45, color: "rgba(61,231,255,0.45)" },
                { offset: 1, color: "rgba(61,231,255,1)" },
              ],
            },
          },
        },
        axisLine: {
          roundCap: true,
          lineStyle: {
            width: 10,
            color: [[1, "rgba(61,231,255,0.15)"]],
          },
        },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: { show: false },
        pointer: { show: false },
        anchor: { show: false },
        title: {
          offsetCenter: [0, "28%"],
          color: "rgba(216,246,255,0.55)",
          fontSize: 12,
        },
        detail: {
          valueAnimation: true,
          offsetCenter: [0, "-8%"],
          formatter: `{value}%`,
          color: "#fff",
          fontSize: 22,
          fontFamily: "Orbitron, monospace",
        },
        data: [{ value: Number(v.toFixed(1)), name }],
      },
    ],
  };
}

export function lineOption(categories, values, { unit = "mm" } = {}) {
  return {
    color: CHART_COLORS,
    tooltip: {
      ...tooltipDark,
      trigger: "axis",
      valueFormatter: (v) => `${v} ${unit}`,
    },
    grid: { left: 36, right: 12, top: 20, bottom: 28 },
    xAxis: {
      type: "category",
      data: categories,
      axisLabel: { color: "rgba(216,246,255,0.6)", fontSize: 10 },
      axisLine: { lineStyle: { color: "rgba(61,231,255,0.25)" } },
    },
    yAxis: {
      type: "value",
      axisLabel: { color: "rgba(216,246,255,0.55)", fontSize: 10 },
      splitLine: { lineStyle: { color: "rgba(61,231,255,0.08)" } },
    },
    series: [
      {
        type: "line",
        data: values,
        smooth: false,
        showSymbol: false,
        symbol: "none",
        lineStyle: { width: 1, color: "#3de7ff" },
        areaStyle: {
          color: {
            type: "linear",
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: "rgba(61,231,255,0.35)" },
              { offset: 1, color: "rgba(61,231,255,0.02)" },
            ],
          },
        },
      },
    ],
  };
}

/** 双轴折线：如水位(m) + 雨量(mm) */
export function dualLineOption(categories, seriesA, seriesB) {
  return {
    color: ["#3de7ff", "#69f0ae"],
    legend: {
      top: 0,
      right: 0,
      itemWidth: 10,
      itemHeight: 6,
      textStyle: { color: "rgba(216,246,255,0.65)", fontSize: 11 },
    },
    tooltip: { ...tooltipDark, trigger: "axis" },
    grid: { left: 40, right: 40, top: 28, bottom: 26 },
    xAxis: {
      type: "category",
      data: categories,
      boundaryGap: false,
      axisLabel: { color: "rgba(216,246,255,0.55)", fontSize: 10 },
      axisLine: { lineStyle: { color: "rgba(61,231,255,0.25)" } },
    },
    yAxis: [
      {
        type: "value",
        name: seriesA.unit || "",
        nameTextStyle: { color: "rgba(216,246,255,0.45)", fontSize: 10 },
        axisLabel: { color: "rgba(216,246,255,0.5)", fontSize: 10 },
        splitLine: { lineStyle: { color: "rgba(61,231,255,0.08)" } },
      },
      {
        type: "value",
        name: seriesB.unit || "",
        nameTextStyle: { color: "rgba(216,246,255,0.45)", fontSize: 10 },
        axisLabel: { color: "rgba(216,246,255,0.5)", fontSize: 10 },
        splitLine: { show: false },
      },
    ],
    series: [
      {
        name: seriesA.name,
        type: "line",
        data: seriesA.data,
        smooth: true,
        showSymbol: false,
        lineStyle: { width: 2, color: "#3de7ff" },
        areaStyle: {
          color: {
            type: "linear",
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: "rgba(61,231,255,0.28)" },
              { offset: 1, color: "rgba(61,231,255,0.02)" },
            ],
          },
        },
      },
      {
        name: seriesB.name,
        type: "line",
        yAxisIndex: 1,
        data: seriesB.data,
        smooth: true,
        showSymbol: false,
        lineStyle: { width: 2, color: "#69f0ae" },
      },
    ],
  };
}
